import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  Bot, Car, ChevronDown, ClipboardList, ExternalLink, Eye, FolderKanban, FolderLock, GraduationCap, History, Home, KeyRound, QrCode,
  LayoutDashboard, Link2, LogOut, Menu, Monitor, MonitorSmartphone, Moon, Plus, Receipt, Search, SlidersHorizontal, Sun, Ticket, Users,
  Vault, X,
} from 'lucide-react'
import { createPortal } from 'react-dom'
import { uitloggen } from '../lib/api'
import { initialen } from '../lib/opmaak'
import { navigeer, useKop, useRoute } from '../lib/router'
import { meldProbleem, useStaat, zet } from '../lib/staat'
import { kiesThema, THEMA_LABELS, useThema, type ThemaKeuze } from '../lib/thema'
import { Knop, Logo } from './ui'
import Zoeken from './Zoeken'
import Meldingen from './Meldingen'
import NieuwTicket from './tickets/NieuwTicket'
import Bevestigen from './Bevestigen'
import { DeelBalk, MeekijkMelding } from './meekijken/InDeShell'

/* ===========================================================================
 *  Het geraamte -- dat van het dashboard, zoals in Projects
 *
 *  Een balk van 48 hoog met links de menuknop en het logo, daarnaast de
 *  naam van het scherm, en rechts zoeken, "Probleem melden" en wie je bent.
 *  De menuknop opent hetzelfde grote menu als in het dashboard.
 *
 *  Sneltoetsen: Ctrl+K of / zoeken, N een probleem melden, Esc dicht.
 * =========================================================================== */

export default function Shell({ children }: { children: ReactNode }) {
  const zoekOpen = useStaat((s) => s.zoekOpen)
  const melden = useStaat((s) => s.melden)
  const bevestigen = useStaat((s) => s.bevestigen)
  const { pad } = useRoute()
  const kop = useKop()
  const [menu, setMenu] = useState(false)

  useEffect(() => { setMenu(false) }, [pad])

  useEffect(() => {
    const toets = (e: KeyboardEvent) => {
      const doel = e.target as HTMLElement | null
      const typt = doel && (doel.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(doel.tagName))
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        zet({ zoekOpen: true })
        return
      }
      if (typt || e.ctrlKey || e.metaKey || e.altKey) return
      if (e.key === '/') { e.preventDefault(); zet({ zoekOpen: true }) }
      if (e.key.toLowerCase() === 'n') { e.preventDefault(); meldProbleem() }
    }
    window.addEventListener('keydown', toets)
    return () => window.removeEventListener('keydown', toets)
  }, [])

  return (
    <div className="shell">
      <header className="topbalk">
        <button type="button" className="menuknop" aria-expanded={menu} aria-label="Menu"
          onClick={() => setMenu((x) => !x)}>
          {menu ? <X size={18} /> : <Menu size={18} />}
        </button>
        <button type="button" className="topbalk-merk" onClick={() => navigeer('/')} title="Naar je overzicht">
          <Logo breedte={96} />
          <span className="topbalk-app">IT</span>
        </button>
        {kop.titel && (
          <div className="topbalk-plek">
            <span className="topbalk-punt">·</span>
            <b>{kop.titel}</b>
            {kop.sub && <span className="topbalk-sub">{kop.sub}</span>}
          </div>
        )}
        <span className="topbalk-rek" />
        <div className="topbalk-rechts">
          <button type="button" className="search-trigger" onClick={() => zet({ zoekOpen: true })} aria-label="Zoeken">
            <Search size={15} />
            <span className="zoek-label">Zoeken…</span>
            <kbd>Ctrl K</kbd>
          </button>
          <Knop soort="hoofd" icoon={<Plus size={16} />} onClick={() => meldProbleem()} title="Een probleem melden (N)">
            <span className="verberg-smal">Probleem melden</span>
          </Knop>
          <IkMenu />
        </div>
      </header>
      <DeelBalk />

      <main id="inhoud" className="werkvlak">{children}</main>

      {menu && <Lanceerder onSluit={() => setMenu(false)} />}
      {melden && <NieuwTicket {...melden} onSluit={() => zet({ melden: null })} />}
      {zoekOpen && <Zoeken />}
      {bevestigen && <Bevestigen />}
      <MeekijkMelding />
      <Meldingen />
    </div>
  )
}

/* ------------------------------------------------------------------ *
 *  Het grote menu, zoals in het dashboard
 * ------------------------------------------------------------------ */

interface Plek { naar: string; label: string; icoon: ReactNode; extern?: boolean }

function Lanceerder({ onSluit }: { onSluit: () => void }) {
  const beheerder = useStaat((s) => s.beheerder)
  const instellingen = useStaat((s) => s.instellingen)
  const ontwikkelaar = useStaat((s) => s.ontwikkelaar)
  const { pad } = useRoute()
  const [filter, setFilter] = useState('')
  const veld = useRef<HTMLInputElement>(null)

  useEffect(() => {
    veld.current?.focus()
    const toets = (e: KeyboardEvent) => { if (e.key === 'Escape') onSluit() }
    window.addEventListener('keydown', toets)
    return () => window.removeEventListener('keydown', toets)
  }, [onSluit])

  const groepen = useMemo(() => {
    const g: { titel: string; plekken: Plek[] }[] = [
      { titel: 'Voor jou', plekken: [
        { naar: '/', label: 'Overzicht', icoon: <Home size={17} /> },
        { naar: '/tickets', label: beheerder ? 'Tickets' : 'Mijn tickets', icoon: <Ticket size={17} /> },
        { naar: '/kluis', label: beheerder ? 'Kluis' : 'Mijn codes', icoon: <Vault size={17} /> },
        { naar: '/links', label: 'Links', icoon: <Link2 size={17} /> },
        { naar: '/toegang', label: beheerder ? 'Toegang' : 'Mijn toegang', icoon: <FolderLock size={17} /> },
      ] },
      { titel: 'Inventaris', plekken: [
        { naar: '/inventaris/apparaten', label: 'Apparaten', icoon: <Monitor size={17} /> },
        { naar: '/inventaris/sleutels', label: 'Sleutels en passen', icoon: <KeyRound size={17} /> },
        { naar: '/inventaris/autos', label: 'Auto\'s', icoon: <Car size={17} /> },
        ...(beheerder ? [{ naar: '/inventaris/abonnementen', label: 'Abonnementen', icoon: <Receipt size={17} /> }] : []),
      ] },
      ...(beheerder ? [{ titel: 'Beheer', plekken: [
        { naar: '/mensen', label: 'Mensen', icoon: <Users size={17} /> },
        { naar: '/stickers', label: 'Stickers printen', icoon: <QrCode size={17} /> },
        { naar: '/overdracht', label: 'Overdracht oude IT-partij', icoon: <ClipboardList size={17} /> },
        { naar: '/logboek', label: 'Logboek', icoon: <History size={17} /> },
      ] }] : []),
      /* Niet onder Beheer: dat is it.beheren (per persoon), en dit hoort bij
         wie in het oude dashboard ontwikkelaar of management was. */
      ...(instellingen ? [{ titel: 'Instellingen', plekken: [
        { naar: '/instellingen', label: 'AI, inkoop en tweede stap', icoon: <SlidersHorizontal size={17} /> },
      ] }] : []),
      /* Namens kijken en meekijken (0244): wie keek als wie, en stoppen. */
      ...(ontwikkelaar ? [{ titel: 'Ontwikkelaar', plekken: [
        { naar: '/kijk-sessies', label: 'Kijk-sessies', icoon: <Eye size={17} /> },
      ] }] : []),
      { titel: 'Truckwash 1 Group', plekken: [
        { naar: 'https://truckwash-workspace.com/', label: 'Het dashboard', icoon: <LayoutDashboard size={17} />, extern: true },
        { naar: 'https://cowork.truckwash-workspace.com/', label: 'Cowork', icoon: <Bot size={17} />, extern: true },
        { naar: 'https://projects.truckwash-workspace.com/', label: 'Projects', icoon: <FolderKanban size={17} />, extern: true },
        { naar: 'https://academy.truckwash-workspace.com/', label: 'Academy', icoon: <GraduationCap size={17} />, extern: true },
      ] },
    ]
    const f = filter.trim().toLowerCase()
    return f ? g.map((x) => ({ ...x, plekken: x.plekken.filter((p) => p.label.toLowerCase().includes(f)) })).filter((x) => x.plekken.length) : g
  }, [beheerder, instellingen, ontwikkelaar, filter])

  const huidig = (naar: string) => (naar === '/' ? pad === '/' : pad.startsWith(naar))

  function ga(p: Plek) {
    if (p.extern) { window.open(p.naar, '_blank', 'noopener,noreferrer'); return }
    onSluit()
    navigeer(p.naar)
  }

  return createPortal(
    <>
      <div className="launcher-sluier" onClick={onSluit} />
      <nav className="launcher" aria-label="Alles in IT">
        <div className="launcher-kop">
          <h2><MonitorSmartphone size={16} /> IT</h2>
          <p>{beheerder ? 'Tickets, de kluis, de inventaris en de toegang van het hele bedrijf.' : 'Je tickets, je codes, je spullen en waar je bij kunt.'}</p>
          <div className="zoekveld launcher-filter">
            <Search size={15} />
            <input ref={veld} value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter dit menu…"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const eerste = groepen[0]?.plekken[0]
                  if (eerste) ga(eerste)
                }
              }} />
          </div>
        </div>
        <div className="launcher-body">
          {groepen.map((g) => (
            <div key={g.titel} className="launcher-groep">
              <h3>{g.titel}</h3>
              {g.plekken.map((p) => (
                <button key={p.naar} type="button" className="launcher-item" aria-current={huidig(p.naar) ? 'page' : undefined}
                  onClick={() => ga(p)}>
                  {p.icoon}
                  <span className="naam">{p.label}</span>
                  {p.extern && <ExternalLink size={13} />}
                </button>
              ))}
            </div>
          ))}
          {!groepen.length && <p className="stil">Niets gevonden voor “{filter}”.</p>}
        </div>
      </nav>
    </>,
    document.body,
  )
}

/* ------------------------------------------------------------------ *
 *  Wie je bent, het thema, en weg
 * ------------------------------------------------------------------ */

function IkMenu() {
  const ik = useStaat((s) => s.ik)!
  const beheerder = useStaat((s) => s.beheerder)
  const { keuze } = useThema()
  const [open, setOpen] = useState(false)
  const knop = useRef<HTMLButtonElement>(null)
  const [plek, setPlek] = useState<{ top: number; right: number } | null>(null)

  useEffect(() => {
    if (!open || !knop.current) return
    const r = knop.current.getBoundingClientRect()
    setPlek({ top: r.bottom + 6, right: window.innerWidth - r.right })
    const toets = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', toets)
    return () => window.removeEventListener('keydown', toets)
  }, [open])

  const ICOON: Record<ThemaKeuze, ReactNode> = { systeem: <Monitor size={15} />, licht: <Sun size={15} />, donker: <Moon size={15} /> }

  return (
    <>
      <button ref={knop} type="button" className={`menu-trigger ${open ? 'open' : ''}`} onClick={() => setOpen((x) => !x)}
        aria-label="Jouw menu">
        <span className="menu-av">{initialen(ik.naam)}</span>
        <ChevronDown size={14} className="pijl" />
      </button>
      {open && plek && createPortal(
        <>
          <div className="uitklap-vang" onMouseDown={() => setOpen(false)} />
          <div className="menu-panel" style={{ top: plek.top, right: plek.right }}>
            <div className="menu-persoon-kop">
              <strong>{ik.naam}</strong>
              <span>{[beheerder ? 'IT-beheer' : '', ik.email].filter(Boolean).join(' · ')}</span>
            </div>
            <div className="menu-scheiding" />
            <div className="menu-groep-titel">Weergave</div>
            {(['systeem', 'licht', 'donker'] as ThemaKeuze[]).map((k) => (
              <button key={k} type="button" className={`menu-item ${keuze === k ? 'aan' : ''}`} onClick={() => kiesThema(k)}>
                <span className="ico">{ICOON[k]}</span>
                <span className="tekst"><strong>{THEMA_LABELS[k]}</strong></span>
                {keuze === k && <span className="menu-vink">✓</span>}
              </button>
            ))}
            <div className="menu-scheiding" />
            <a className="menu-item" href="https://truckwash-workspace.com/" target="_blank" rel="noopener noreferrer">
              <span className="ico"><LayoutDashboard size={15} /></span>
              <span className="tekst"><strong>Naar het dashboard</strong></span>
            </a>
            <button type="button" className="menu-item danger" onClick={() => { void uitloggen().then(() => location.assign('/')) }}>
              <span className="ico"><LogOut size={15} /></span>
              <span className="tekst"><strong>Uitloggen</strong></span>
            </button>
          </div>
        </>,
        document.body,
      )}
    </>
  )
}
